import { centsToFormattedDollars } from '@fruitful/fruitils';
import {
    type MoneyMapRuleGroup,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { orderBy } from 'lodash';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Icon,
    redactPrivate,
    TargetIcon,
    XStack,
    YStack,
} from 'smoothie';

import {
    getSyntheticGoalPriorityFromActual,
    prioritySortRulesAndGroups,
} from '../utils/moneyMapRuleUtils';
import {
    MoneyMapRulePercentage,
    MoneyMapRulePriority,
} from './MoneyMapAccountRule';

interface MoneyMapGoalSummaryRedesignProps {
    moneyMap: MoneyMapWithRulesAndAccounts;
    rules: MoneyMapRuleWithAccountDetails[];
}

const GoalAmount = ({ rule }: { rule: MoneyMapRuleWithAccountDetails }) => {
    if (rule.targetBalanceCents == null || rule.targetBalanceCents <= 0) {
        return null;
    }

    return (
        <XStack gap={4} alignItems="center" {...redactPrivate()}>
            <Icon icon={TargetIcon} size={16} />
            <BodyMediumRegular>
                {centsToFormattedDollars(rule.targetBalanceCents, false)}
            </BodyMediumRegular>
        </XStack>
    );
};

const GoalContributionLimits = ({
    rule,
}: {
    rule: MoneyMapRuleWithAccountDetails;
}) => {
    const dollarLimits = [
        rule.monthlyContributionMaxCents != null &&
        rule.monthlyContributionMaxCents > 0
            ? `${centsToFormattedDollars(rule.monthlyContributionMaxCents, false)}/mo`
            : null,
        rule.yearlyContributionMaxCents != null &&
        rule.yearlyContributionMaxCents > 0
            ? `${centsToFormattedDollars(rule.yearlyContributionMaxCents, false)}/yr`
            : null,
    ].filter((value): value is string => value != null);

    const parts = [
        dollarLimits.length > 0 ? `Max ${dollarLimits.join(' • ')}` : null,
        rule.moneyMapAccount?.hasRetirementContributionLimit === true
            ? 'Until annual IRA limit'
            : null,
    ].filter((value): value is string => value != null);

    return parts.length > 0 ? (
        <BodySmallRegular color="$gray1000" {...redactPrivate()}>
            {parts.join(' • ')}
        </BodySmallRegular>
    ) : null;
};

const TopLevelGoalRow = ({
    priority,
    description,
    rule,
}: {
    priority: number;
    description: string;
    rule: MoneyMapRuleWithAccountDetails;
}) => {
    return (
        <XStack gap={12} alignItems="center" padding={16}>
            <MoneyMapRulePriority priority={priority} />
            <YStack flex={1} gap={4}>
                <BodyMediumRegular>{description}</BodyMediumRegular>
                <GoalContributionLimits rule={rule} />
            </YStack>
            <GoalAmount rule={rule} />
        </XStack>
    );
};

const GroupGoalRow = ({ rule }: { rule: MoneyMapRuleWithAccountDetails }) => {
    return (
        <XStack
            gap={12}
            alignItems="center"
            padding={12}
            borderRadius={16}
            backgroundColor="$white"
        >
            <MoneyMapRulePercentage percentage={rule.percentage ?? 1} />
            <YStack flex={1} gap={4}>
                <BodyMediumRegular>{rule.description}</BodyMediumRegular>
                <GoalContributionLimits rule={rule} />
            </YStack>
            <GoalAmount rule={rule} />
        </XStack>
    );
};

const GoalGroupSummary = ({
    group,
    rules,
    moneyMap,
}: {
    group: MoneyMapRuleGroup;
    rules: MoneyMapRuleWithAccountDetails[];
    moneyMap: MoneyMapWithRulesAndAccounts;
}) => {
    const sortedRules = orderBy(rules, 'priority');
    const firstRule = sortedRules[0];
    const priority = getSyntheticGoalPriorityFromActual(
        group.priority,
        moneyMap,
    );

    if (firstRule == null) {
        return null;
    }

    if (sortedRules.length === 1) {
        return (
            <TopLevelGoalRow
                priority={priority}
                description={firstRule.description}
                rule={firstRule}
            />
        );
    }

    return (
        <YStack gap={12} padding={16}>
            <XStack gap={12} alignItems="center">
                <MoneyMapRulePriority priority={priority} />
                <BodyMediumRegular flex={1}>{group.name}</BodyMediumRegular>
            </XStack>
            <YStack gap={8}>
                {sortedRules.map((rule) => (
                    <GroupGoalRow key={rule.id} rule={rule} />
                ))}
            </YStack>
        </YStack>
    );
};

export const MoneyMapGoalSummaryRedesign = ({
    moneyMap,
    rules,
}: MoneyMapGoalSummaryRedesignProps) => {
    const groupsWithRules = moneyMap.moneyMapRuleGroups.filter((group) =>
        rules.some((rule) => rule.moneyMapRuleGroupId === group.id),
    );
    const topLevelItems = prioritySortRulesAndGroups(rules, groupsWithRules);

    return (
        <YStack gap={4} backgroundColor="$gray100">
            {topLevelItems.map((item) => {
                if (item.entityType === 'group') {
                    const group = item as MoneyMapRuleGroup;
                    return (
                        <GoalGroupSummary
                            key={group.id}
                            group={group}
                            rules={rules.filter(
                                (rule) => rule.moneyMapRuleGroupId === group.id,
                            )}
                            moneyMap={moneyMap}
                        />
                    );
                }

                const rule = item as MoneyMapRuleWithAccountDetails;
                return (
                    <TopLevelGoalRow
                        key={rule.id}
                        priority={getSyntheticGoalPriorityFromActual(
                            rule.priority,
                            moneyMap,
                        )}
                        description={rule.description}
                        rule={rule}
                    />
                );
            })}
        </YStack>
    );
};
